import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  FormHelperText,
  Link,
  TextField,
} from '@mui/material'

import { ReactComponent as EyeIcon } from '../../../assets/icons/eye.svg'
import { ReactComponent as EyeClosedIcon } from '../../../assets/icons/eye-closed.svg'
import { ReactComponent as WarningCircleFillIcon } from '../../../assets/icons/warning-circle-fill.svg'

import { getErrorProp, getHelperTextProp } from '../../../common/formProps'
import useTranslations from '../../../i18n/useTranslations'

import useStyles from '../styles'

import passwordValidationSchema from './validationSchema'

const propTypes = {
  hasBadCurrentPassword: PropTypes.bool,
  updatePassword: PropTypes.func.isRequired,
  hasSucceeded: PropTypes.bool,
  hasFailed: PropTypes.bool,
}

const PasswordEditorView = ({
  hasBadCurrentPassword,
  updatePassword,
  hasSucceeded,
  hasFailed,
}) => {
  const t = useTranslations()
  const classes = useStyles()

  const [isEditingEnabled, enableEditing] = useState(false)
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  })

  const { touched, errors, ...formik } = useFormik({
    validationSchema: passwordValidationSchema,
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    onSubmit: async values => updatePassword(values),
  })

  useEffect(
    () => {
      if (!hasSucceeded) return

      enableEditing(false)

      formik.resetForm()
    },
    // IMPORTANT Formik referential instability would cause a re-render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasSucceeded],
  )

  useEffect(
    () => {
      if (!hasBadCurrentPassword) return

      formik.setFieldError('currentPassword', { key: 'user-zone:bad-password' })
    },
    // IMPORTANT Formik referential instability would cause a re-render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasBadCurrentPassword, t],
  )

  const createShowPasswordHandler = passwordId => event => {
    const newShowPasswords = {
      ...showPasswords,
      [passwordId]: !showPasswords[passwordId],
    }
    setShowPasswords(newShowPasswords)
  }

  return isEditingEnabled ? (
    <form
      onSubmit={formik.handleSubmit}
      onReset={e => {
        enableEditing(false)
        formik.handleReset(e)
      }}
    >
      <Box className={classes.changePasswordContainer}>
        <TextField
          label={t('noun:current-password')}
          type={showPasswords.currentPassword ? 'text' : 'password'}
          id="currentPassword"
          className="input-smaller"
          error={getErrorProp('currentPassword', errors, touched)}
          helperText={getHelperTextProp('currentPassword', errors, touched, t)}
          InputProps={{
            ...formik.getFieldProps('currentPassword'),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={createShowPasswordHandler('currentPassword')}
                  aria-label="toggle password visibility"
                  size="large"
                >
                  {showPasswords.currentPassword ? (
                    <EyeIcon />
                  ) : (
                    <EyeClosedIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label={t('noun:new-password')}
          type={showPasswords.newPassword ? 'text' : 'password'}
          id="newPassword"
          className="input-smaller"
          error={getErrorProp('newPassword', errors, touched)}
          helperText={getHelperTextProp('newPassword', errors, touched, t)}
          InputProps={{
            ...formik.getFieldProps('newPassword'),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={createShowPasswordHandler('newPassword')}
                  aria-label="toggle password visibility"
                  size="large"
                >
                  {showPasswords.newPassword ? <EyeIcon /> : <EyeClosedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label={t('user-zone:confirm-password')}
          type={showPasswords.confirmNewPassword ? 'text' : 'password'}
          id="confirmNewPassword"
          name="confirmNewPassword"
          className="input-smaller"
          error={getErrorProp('confirmNewPassword', errors, touched)}
          helperText={getHelperTextProp(
            'confirmNewPassword',
            errors,
            touched,
            t,
          )}
          InputProps={{
            ...formik.getFieldProps('confirmNewPassword'),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={createShowPasswordHandler('confirmNewPassword')}
                  aria-label="toggle password visibility"
                  size="large"
                >
                  {showPasswords.confirmNewPassword ? (
                    <EyeIcon />
                  ) : (
                    <EyeClosedIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormHelperText className={classes.helperText}>
          <WarningCircleFillIcon />
          {t('user-zone:password-msg-info')}
        </FormHelperText>
      </Box>
      <Box className={classes.changePasswordActionButtons}>
        <Button type="reset" variant="outlined" size="small">
          {t('action:cancel')}
        </Button>

        <Button type="submit" size="small" disabled={!formik.isValid}>
          {t('action:save')}
        </Button>
      </Box>
      {hasFailed ? (
        <Alert severity="error" className={classes.alertError}>
          {t('user-zone:password-error')}
        </Alert>
      ) : null}
    </form>
  ) : (
    <Box className={classes.currentPasswordContainer}>
      <Link
        component="button"
        onClick={() => {
          enableEditing(true)
        }}
      >
        {t('user-zone:change-password')}
      </Link>
      {hasSucceeded ? (
        <Alert severity="success" className={classes.alertSuccess}>
          {t('user-zone:password-saved')}
        </Alert>
      ) : null}
    </Box>
  )
}
PasswordEditorView.propTypes = propTypes

export default PasswordEditorView
